/**
 * Phase HR-JOIN-001 — Employee Joining Workflow list view.
 * Route: /hr/joining
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Progress,
  message,
} from 'antd'
import {
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

const { Text } = Typography
import {
  joiningApi,
  JOINING_STATUSES,
  JOINING_EMPLOYEE_TYPES,
  type JoiningListItem,
  type JoiningStatus,
  type JoiningEmployeeType,
} from '@/features/hr/joiningApi'
import { plantsApi } from '@/features/masters/plants/api'
import { departmentsApi } from '@/features/masters/departments/api'
import PhotosNeededWidget from '@/pages/hr/widgets/PhotosNeededWidget'

const STATUS_COLOR: Record<JoiningStatus, string> = {
  DRAFT: 'default',
  DOCUMENT_PENDING: 'gold',
  DOCUMENT_RECEIVED: 'cyan',
  VERIFICATION_PENDING: 'orange',
  VERIFIED: 'lime',
  EMP_CODE_GENERATED: 'blue',
  PAYROLL_PENDING: 'geekblue',
  STATUTORY_PENDING: 'purple',
  BIOMETRIC_PENDING: 'magenta',
  INDUCTION_PENDING: 'volcano',
  APPROVAL_PENDING: 'gold',
  ACTIVE: 'green',
  HOLD: 'default',
  REJECTED: 'red',
}

export default function JoiningListPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<JoiningStatus | undefined>()
  const [employeeType, setEmployeeType] = useState<JoiningEmployeeType | undefined>()
  const [search, setSearch] = useState('')
  const [includeActive, setIncludeActive] = useState(false)
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['joining-list', { status, employeeType, search, includeActive, page }],
    queryFn: () =>
      joiningApi.list({
        status,
        employee_type: employeeType,
        search: search || undefined,
        include_active: includeActive,
        page,
        size: 20,
      }),
  })

  const columns = [
    {
      title: 'Emp Code',
      dataIndex: 'emp_code',
      // Avoid duplicate DRAFT tag — the Status column already shows DRAFT.
      // Here we just show a muted "Pending" placeholder so the column has
      // semantic value without visual duplication.
      render: (v: string | null) =>
        !v || v.startsWith('DRAFT-') ? (
          <Text type="secondary" italic>
            Pending
          </Text>
        ) : (
          <Text code copyable={{ tooltips: ['Copy code', 'Copied'] }}>
            {v}
          </Text>
        ),
      width: 140,
    },
    {
      title: 'Name',
      dataIndex: 'full_name',
      ellipsis: true,
      width: 180,
      render: (v: string) => (
        <Tooltip title={v} mouseEnterDelay={0.5}>
          <Text strong>{v}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'joining_employee_type',
      render: (v: string | null) =>
        v ? <Tag>{v.replace(/_/g, ' ')}</Tag> : <Text type="secondary">—</Text>,
      width: 170,
    },
    {
      title: 'Plant',
      dataIndex: 'plant_name',
      ellipsis: true,
      width: 110,
      render: (v: string) => <Tooltip title={v} mouseEnterDelay={0.5}>{v}</Tooltip>,
    },
    {
      title: 'Dept',
      dataIndex: 'department_name',
      ellipsis: true,
      width: 130,
      render: (v: string) => <Tooltip title={v} mouseEnterDelay={0.5}>{v}</Tooltip>,
    },
    {
      title: 'Joining',
      dataIndex: 'joining_date',
      render: (v: string | null) =>
        v ? <Text style={{ whiteSpace: 'nowrap' }}>{dayjs(v).format('DD MMM YYYY')}</Text> : '—',
      width: 110,
    },
    {
      title: 'Status',
      dataIndex: 'joining_status',
      render: (v: JoiningStatus, r: JoiningListItem) => (
        <Space size={4} direction="vertical" style={{ lineHeight: 1.2 }}>
          <Tag color={STATUS_COLOR[v]} style={{ margin: 0 }}>
            {v.replace(/_/g, ' ')}
          </Tag>
          {v === 'ACTIVE' && r.completion_percent < 100 && (
            <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>
              backfilling
            </Tag>
          )}
        </Space>
      ),
      width: 170,
    },
    {
      title: 'Progress',
      dataIndex: 'completion_percent',
      render: (v: number) => (
        <Tooltip title={`${v}% of required activation items complete`}>
          <Progress
            percent={v}
            size="small"
            status={v === 100 ? 'success' : 'active'}
            style={{ width: 90, margin: 0 }}
          />
        </Tooltip>
      ),
      width: 110,
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right' as const,
      width: 90,
      render: (_: unknown, r: JoiningListItem) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/hr/joining/${r.id}`)
          }}
        >
          Open <RightOutlined style={{ fontSize: 11 }} />
        </Button>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <PhotosNeededWidget />
      <Card
        title={
          <Space>
            <UserAddOutlined />
            <span>Employee Joining</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} loading={isRefetching} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              New Joining
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16, width: '100%' }} wrap>
          <Input.Search
            placeholder="Search by name or emp code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={() => {
              setPage(1)
              refetch()
            }}
            style={{ width: 280 }}
            allowClear
          />
          <Select
            placeholder="Status"
            style={{ width: 200 }}
            allowClear
            value={status}
            onChange={(v) => {
              setStatus(v)
              setPage(1)
            }}
            options={JOINING_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))}
          />
          <Select
            placeholder="Employee Type"
            style={{ width: 220 }}
            allowClear
            value={employeeType}
            onChange={(v) => {
              setEmployeeType(v)
              setPage(1)
            }}
            options={JOINING_EMPLOYEE_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, ' ') }))}
          />
          <Tooltip title="Show already-ACTIVE employees too, so HR can backfill missing data via the joining form (KYC, photo, bank, nominee, etc.).">
            <Space size={6}>
              <Switch
                size="small"
                checked={includeActive}
                onChange={(checked) => {
                  setIncludeActive(checked)
                  setPage(1)
                }}
              />
              <Text style={{ fontSize: 13 }}>
                Show ACTIVE workers
              </Text>
            </Space>
          </Tooltip>
        </Space>

        <Table<JoiningListItem>
          rowKey="id"
          loading={isLoading}
          dataSource={data?.items ?? []}
          columns={columns}
          scroll={{ x: 1180 }}
          onRow={(r) => ({
            onClick: () => navigate(`/hr/joining/${r.id}`),
            style: { cursor: 'pointer' },
          })}
          rowClassName={() => 'joining-row-hover'}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical" size={4}>
                    <Text strong>No joining drafts yet</Text>
                    <Text type="secondary">
                      Click <b>New Joining</b> to start onboarding an employee
                    </Text>
                  </Space>
                }
                style={{ padding: '32px 0' }}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateOpen(true)}
                >
                  New Joining
                </Button>
              </Empty>
            ),
          }}
          pagination={{
            current: page,
            pageSize: 20,
            total: data?.total ?? 0,
            onChange: setPage,
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
        />
        <style>{`
          .joining-row-hover:hover { background: #fafafa; }
          .joining-row-hover td { transition: background 150ms ease-out; }
        `}</style>
      </Card>

      <NewJoiningModal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false)
          navigate(`/hr/joining/${id}`)
        }}
      />
    </div>
  )
}

interface NewJoiningModalProps {
  open: boolean
  onCancel: () => void
  onCreated: (id: string) => void
}

function NewJoiningModal({ open, onCancel, onCreated }: NewJoiningModalProps) {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const { data: plantsData } = useQuery({
    queryKey: ['plants-for-joining'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })

  // Match EmployeesPage pattern: load ALL departments upfront (no filter).
  // Backend pagination of departments at 500 covers any realistic mill setup.
  // We client-side filter by selectedPlantId for the dropdown options.
  const { data: deptsData } = useQuery({
    queryKey: ['departments-all-for-joining'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500 }),
  })

  const selectedPlantId = Form.useWatch('plant_id', form) as string | undefined
  const filteredDepts = (deptsData?.items ?? []).filter(
    (d) => !selectedPlantId || d.plant_id === selectedPlantId,
  )

  const onFinish = async (values: Record<string, unknown>) => {
    setSubmitting(true)
    try {
      const body = {
        ...values,
        date_of_birth: dayjs(values.date_of_birth as dayjs.Dayjs).format('YYYY-MM-DD'),
        joining_date: dayjs(values.joining_date as dayjs.Dayjs).format('YYYY-MM-DD'),
      } as Parameters<typeof joiningApi.create>[0]
      const res = await joiningApi.create(body)
      message.success(`Draft created (${res.emp_code})`)
      onCreated(res.id)
      form.resetFields()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err.response?.data?.detail ?? 'Failed to create draft')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="New Joining"
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText="Create Draft"
      confirmLoading={submitting}
      width={640}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ category: 'PERMANENT', joining_employee_type: 'PRODUCTION_WORKER' }}
      >
        <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="last_name" label="Last Name">
          <Input />
        </Form.Item>
        <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'MALE', label: 'Male' },
              { value: 'FEMALE', label: 'Female' },
              { value: 'OTHER', label: 'Other' },
            ]}
          />
        </Form.Item>
        <Form.Item name="date_of_birth" label="Date of Birth" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
        </Form.Item>
        <Form.Item
          name="phone"
          label="Mobile"
          rules={[
            { required: true },
            { pattern: /^[6-9]\d{9}$/, message: 'Valid 10-digit Indian mobile required' },
          ]}
        >
          <Input maxLength={10} />
        </Form.Item>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select
            placeholder="Select plant"
            options={(plantsData?.items ?? []).map((p) => ({ value: p.id, label: p.name }))}
            showSearch
            optionFilterProp="label"
            onChange={() => form.setFieldValue('department_id', undefined)}
          />
        </Form.Item>
        <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
          <Select
            placeholder={selectedPlantId ? 'Select department' : 'Pick plant first'}
            disabled={!selectedPlantId}
            options={filteredDepts.map((d) => ({ value: d.id, label: d.name }))}
            showSearch
            optionFilterProp="label"
            notFoundContent={
              deptsData === undefined
                ? 'Loading…'
                : filteredDepts.length === 0
                ? `No departments for this plant (${deptsData?.items?.length ?? 0} total in system)`
                : 'No match'
            }
          />
        </Form.Item>
        <Form.Item name="joining_date" label="Joining Date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
        </Form.Item>
        <Form.Item name="category" label="Category" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'PERMANENT', label: 'Permanent' },
              { value: 'CONTRACT', label: 'Contract' },
              { value: 'TRAINEE', label: 'Trainee' },
              { value: 'CONSULTANT', label: 'Consultant' },
            ]}
          />
        </Form.Item>
        <Form.Item name="joining_employee_type" label="Employee Type" rules={[{ required: true }]}>
          <Select
            options={JOINING_EMPLOYEE_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, ' ') }))}
          />
        </Form.Item>
        <Form.Item name="joining_notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
